<script>
import Datepicker from 'vuejs-datepicker'
import VueTagsInput from '@johmun/vue-tags-input'
import store from '@state/store'
import { debounce } from 'debounce'
import isArray from 'lodash/isArray'
import { prepareTagsForVueTagsInput } from '@/src/events/utils'

const initialFiltersState = {
  from_date: '',
  to_date: '',
  with_ongoing: true,

  ongoing: false,
  is_available: false,
  location: '',
  cause_ids: [],
  tag_ids: [],
  keyword: '',
}

const initialTagsState = {
  items: [],
  input: '',
  selected: [],
}

export default {
  name: 'EventsFilter',
  components: { Datepicker, VueTagsInput },
  props: {
    companyId: {
      type: Number,
      required: false,
      default: null,
    },
    organizationId: {
      type: Number,
      required: false,
      default: null,
    },
    coHostOrganizationId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    filters: { ...initialFiltersState },
    causes: { ...initialTagsState },
    tags: { ...initialTagsState },
  }),
  computed: {
    causesInput() {
      return this.causes.input
    },
    tagsInput() {
      return this.tags.input
    },
    date() {
      return {
        from_date: this.$store.state.events.filters.from_date,
        to_date: this.$store.state.events.filters.to_date,
      }
    },
  },
  watch: {
    causesInput: 'getEventCauses',
    tagsInput: 'getEventTags',
    date() {
      this.filters = { ...this.filters, ...this.date }
    },
    filters: {
      async handler() {
        const filledFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => {
            if (isArray(value) && !value.length) return false
            return !!value
          })
        )

        await store.dispatch('events/fetchFilteredEvents', {
          companyId: this.companyId,
          organizationId: this.organizationId,
          coHostOrganizationId: this.coHostOrganizationId,
          filters: filledFilters,
        })
      },
      deep: true,
    },
  },
  created() {
    this.setFiltersFromStore()
    this.getEventCauses()
  },
  methods: {
    setFiltersFromStore() {
      this.filters = {
        ...this.filters,
        ...this.$store.state.events.filters,
      }
    },

    clearFilters() {
      this.filters = { ...initialFiltersState }
      this.causes = { ...initialTagsState }
      this.tags = { ...initialTagsState }
    },
    setFilterField: debounce(async function (e, field, minLength = 0) {
      const value = (e.target && e.target.value) || e
      this.filters[field] = value.length > minLength ? value : ''
    }, 500),

    getEventCauses() {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        store
          .dispatch('companies/fetchCauses')
          .then((response) => {
            this.causes.items = response.data.map((a) => {
              return { text: a.name, id: a.id }
            })
          })
          .catch(() => console.warn('Oh. Something went wrong'))
      }, 500)
    },

    updateEventCauses(newCauses) {
      this.causes.selected = newCauses
      this.filters.cause_ids = this.causes.selected.map(({ id }) => id)
    },

    getEventTags: debounce(async function getEventTags() {
      if (!this.tagsInput) return

      await store
        .dispatch('events/fetchFilteredTags', {
          startingWith: this.tagsInput,
        })
        .then(({ data }) => {
          if (!data) return
          this.tags.items = prepareTagsForVueTagsInput(data)
        })
    }, 500),
    updateEventTags(newTags) {
      this.tags.items = []
      this.tags.selected = newTags
      this.filters.tag_ids = this.tags.selected.map(({ id }) => id)
    },
  },
}
</script>

<template>
  <div>
    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <button
        class="block w-48 h-12 px-6 mb-4 text-lg text-left button secondary-button"
        >+ Show Filters</button
      >

      <template slot="popover">
        <div class="space-y-3 w-58">
          <div class="flex justify-end">
            <button
              class="inline-flex items-center text-sml button secondary-button"
              @click="clearFilters"
            >
              <BaseIcon name="close" class="mr-1" />
              <span>Clear Filters</span>
            </button>
          </div>

          <div>
            <label class="inline-flex items-center cursor-pointer">
              <input
                v-model="filters.ongoing"
                class="mr-2 checkbox"
                type="checkbox"
              />
              <span class="text-black text-basel">Ongoing</span>
            </label>
          </div>

          <div>
            <label class="inline-flex items-center cursor-pointer">
              <input
                v-model="filters.is_available"
                class="mr-2 checkbox"
                type="checkbox"
              />
              <span class="text-black text-basel">
                Show events with openings
              </span>
            </label>
          </div>

          <div>
            <div class="flex items-center justify-between">
              <span class="text-black mr-2.5 text-basel">From:</span>
              <Datepicker
                v-model="filters.from_date"
                format="MM/dd/yy"
                input-class="w-16 text-center border-b border-black cursor-pointer"
              />

              <span class="text-black mx-2.5 text-basel">to</span>

              <Datepicker
                v-model="filters.to_date"
                format="MM/dd/yy"
                :disabled-dates="{ to: filters.from_date }"
                input-class="text-center border-b border-black cursor-pointer w-18"
              />
            </div>
          </div>

          <div>
            <input
              :value="filters.location"
              class="w-full text-center border-b border-black leading-looser"
              placeholder="City, state, zip"
              @input="setFilterField($event, 'location', 2)"
            />
          </div>

          <div>
            <VueTagsInput
              v-model="causes.input"
              :tags="causes.selected"
              :autocomplete-items="causes.items"
              :add-only-from-autocomplete="true"
              :add-on-blur="false"
              placeholder="Filter by event cause"
              :autocomplete-min-length="0"
              @tags-changed="updateEventCauses"
            />
          </div>

          <div>
            <VueTagsInput
              v-model="tags.input"
              :tags="tags.selected"
              :autocomplete-items="tags.items"
              :add-only-from-autocomplete="true"
              :add-on-blur="false"
              placeholder="Filter by event tag"
              @tags-changed="updateEventTags"
            />
          </div>

          <div>
            <input
              :value="filters.keyword"
              class="w-full text-center border-b border-black leading-looser"
              placeholder="Filter by Keyword"
              @input="setFilterField($event, 'keyword', 3)"
            />
          </div>
        </div>
      </template>
    </v-popover>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.vue-tags-input::v-deep {
  .ti-input {
    @apply border-0 border-b border-black rounded-none shadow-none;
  }
  .ti-new-tag-input {
    @apply text-center text-black;
  }
}
/* stylelint-enable */
</style>
